import React from 'react';
import PropTypes from 'prop-types';
import { X, PlayCircle } from 'lucide-react';

/**
 * StartAssessmentModal - Modal for selecting assessment types before starting
 * @param {boolean} isOpen - Whether the modal is visible
 * @param {function} onClose - Handler to close the modal
 * @param {function} onConfirm - Handler when user confirms to start assessment
 * @param {Array<string>} selectedAssessmentTypes - Currently selected assessment types
 * @param {function} onToggleAssessmentType - Handler to toggle assessment type selection
 */
const StartAssessmentModal = ({
  isOpen,
  onClose,
  onConfirm,
  selectedAssessmentTypes,
  onToggleAssessmentType
}) => {
  if (!isOpen) return null;

  // Calculate totals
  const totalQuestions = 89 + (selectedAssessmentTypes.includes('family enterprise') ? 80 : 0);
  const estimatedMinutes = Math.round(totalQuestions * 0.5);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-2 sm:p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          {/* Header */}
          <div className="flex justify-between items-start mb-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Start Business Gap Assessment</h2>
              <p className="text-gray-600 mt-2">Select the assessment areas you'd like to evaluate</p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
              aria-label="Close modal"
            >
              <X size={24} />
            </button>
          </div>

          {/* Assessment Type Selection */}
          <div className="space-y-4 mb-6">
            {/* Business Assessment - Always Included */}
            <div className="border-2 border-blue-500 bg-blue-50 rounded-lg p-4">
              <div className="flex items-start">
                <div className="flex items-center h-5 mr-3">
                  <input
                    type="checkbox"
                    checked={true}
                    disabled={true}
                    className="w-5 h-5 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                    aria-label="Business assessment (required)"
                  />
                </div>
                <div className="flex-1">
                  <label className="font-semibold text-gray-900">
                    Business Assessment <span className="text-blue-600 text-sm">(Required)</span>
                  </label>
                  <p className="text-sm text-gray-600 mt-1">
                    Assess your business readiness and attractiveness across financial performance, management, growth potential, and more.
                    <span className="font-medium text-gray-700"> (89 questions, ~45 minutes)</span>
                  </p>
                </div>
              </div>
            </div>

            {/* Family Enterprise - Optional */}
            <div
              className={`border-2 rounded-lg p-4 cursor-pointer transition-colors ${
                selectedAssessmentTypes.includes('family enterprise')
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-300 bg-white hover:border-blue-300'
              }`}
              onClick={() => onToggleAssessmentType('family enterprise')}
            >
              <div className="flex items-start">
                <div className="flex items-center h-5 mr-3">
                  <input
                    type="checkbox"
                    checked={selectedAssessmentTypes.includes('family enterprise')}
                    onChange={() => onToggleAssessmentType('family enterprise')}
                    className="w-5 h-5 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                    aria-label="Family enterprise assessment (optional)"
                  />
                </div>
                <div className="flex-1">
                  <label className="font-semibold text-gray-900 cursor-pointer">
                    Family Enterprise <span className="text-gray-500 text-sm">(Optional)</span>
                  </label>
                  <p className="text-sm text-gray-600 mt-1">
                    Evaluate family governance, succession planning, family communication, and multi-generational wealth transfer for family-owned businesses.
                    <span className="font-medium text-gray-700"> (80 questions, ~40 minutes)</span>
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Summary */}
          <div className="bg-gray-50 rounded-lg p-4 mb-6">
            <div className="flex justify-between items-center">
              <span className="text-gray-700 font-medium">Total Questions:</span>
              <span className="text-2xl font-bold text-blue-600">
                {totalQuestions}
              </span>
            </div>
            <p className="text-sm text-gray-600 mt-2">
              Estimated time: ~{estimatedMinutes} minutes (approximately 30 seconds per question)
            </p>
          </div>

          {/* Actions */}
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="flex-1 px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-50 transition min-h-[44px]"
            >
              Cancel
            </button>
            <button
              onClick={onConfirm}
              className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition inline-flex items-center justify-center min-h-[44px]"
            >
              <PlayCircle size={20} className="mr-2" />
              Start Assessment
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

StartAssessmentModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  selectedAssessmentTypes: PropTypes.arrayOf(PropTypes.string).isRequired,
  onToggleAssessmentType: PropTypes.func.isRequired
};

export default StartAssessmentModal;
